import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useAuth } from '../../../contexts/AuthContext';
import { Loader2, Mail, Lock } from 'lucide-react';

type FormData = {
  email: string;
  password: string;
};

export default function Login() {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsLoading(true);

    try {
      const success = await login(formData.email, formData.password);
      if (success) {
        // Redirection vers la page d'accueil après une connexion réussie
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrorMessage('Login failed. Please check your credentials and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-page-background p-4 sm:p-6 md:p-8">
      {/* Logo et texte d'accueil */}
      <div className="w-full max-w-sm text-center mb-8 flex flex-col items-center">
        <div className="w-16 h-16 mb-5">
          <img
            src="/images/datasset-icon.svg"
            alt="Datasset Logo"
            className="w-full h-full object-contain"
          />
        </div>
        <h1 className="text-2xl font-bold text-text-principal font-display">Welcome Back</h1>
        <p className="text-night-slate mt-1 text-sm">Log in to access your account</p>
      </div>

      {/* Carte du formulaire */}
      <div className="w-full max-w-sm bg-white rounded-xl shadow-card p-6 sm:p-8">
        {errorMessage && (
          <div className="mb-6 p-3 bg-red-50 border border-red-100 rounded-md text-red-600 text-sm">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <div className="relative">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-mist-grey" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-3 py-2 border border-mist-grey rounded-lg focus:ring-2 focus:ring-night-slate focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-mist-grey" />
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                className="w-full pl-10 pr-3 py-2 border border-mist-grey rounded-lg focus:ring-2 focus:ring-night-slate focus:border-transparent"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-night-slate text-white py-2.5 rounded-lg hover:bg-night-slate/90 transition-colors"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Logging in...
              </>
            ) : (
              'Log In'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}

